// Service worker registration with a boot gate: the app does not render until
// this page is known to be running the latest deployed build.
//
// The app shell is precached (see the VitePWA config in vite.config.ts), so a
// repeat open paints the static splash in index.html instantly from local
// cache. That shell may be stale, so index.tsx waits for
// bootWithLatestVersion() before rendering anything interactive:
//
//   1. /version.json (served no-store, a few bytes) is fetched and compared
//      with this bundle's __BUILD_TIME__. Equal → render right away.
//   2. Different → a newer build is deployed. The page navigates once to the
//      same URL with a `riw-fresh` query param, which the service worker
//      answers from the network instead of the precache (see the
//      navigateFallbackDenylist + runtimeCaching entries in vite.config.ts),
//      so the new shell and its chunks load straight from the network — like
//      a first visit, typically a second or two. The splash stays up the
//      whole time; the user never sees the stale build. Meanwhile the worker
//      update check (started in parallel) installs the new build into the
//      precache in the background, so the next open is instant again.
//      Should connectivity drop right then, the worker falls back to the
//      precached shell; that page sees the marker and renders what it has
//      rather than navigating again, so no loop is possible.
//   3. The server could not be asked within the budget (offline, captive
//      portal) → the cached build renders rather than stranding the user on
//      the splash.
//
// A new worker taking over an already-rendered page re-checks version.json:
// if it merely caught up to the build this page already runs, nothing
// happens; if a different build is deployed, the reload waits until the tab
// is hidden with no request in flight — or happens at once while the user
// hasn't interacted yet. The backend can force it via x-riw-min-fe-build.

import { hasPendingRequests } from 'src/utils/pendingRequests';

// Emitted by the riw-build-version plugin in vite.config.ts with the same
// build id as __BUILD_TIME__; served no-store and never precached.
const VERSION_URL = '/version.json';
// Query param that makes the service worker answer a navigation from the
// network (see vite.config.ts). Stripped from the URL again before the app
// renders.
const FRESH_PARAM = 'riw-fresh';
// Network budget for the boot version check. Past it the cached build renders.
const VERSION_CHECK_TIMEOUT_MS = 6 * 1000;
// If a navigation somehow doesn't tear the page down, render anyway.
const RELOAD_FALLBACK_MS = 10 * 1000;
// How long after page load a new worker may still reload the page
// immediately (only while the user hasn't interacted).
const STARTUP_RELOAD_WINDOW_MS = 60 * 1000;
// Periodic update check for long-lived sessions.
const UPDATE_CHECK_INTERVAL_MS = 30 * 60 * 1000;
// Minimum spacing between visibility-triggered update checks.
const VISIBILITY_CHECK_THROTTLE_MS = 5 * 60 * 1000;
// Without a service worker, an incompatible-build signal falls back to a
// plain reload; throttle it (per tab) so a backend that flags this build
// before the new FE has propagated can't cause a reload loop.
const FORCED_RELOAD_AT_KEY = 'riw-forced-reload-at';
const FORCED_RELOAD_THROTTLE_MS = 10 * 60 * 1000;
// Reload recovery for lazy chunks that no longer exist (see below); throttled
// so a genuinely missing asset can't cause a reload loop.
const PRELOAD_ERROR_RELOAD_AT_KEY = 'riw-preload-error-reload-at';
const PRELOAD_ERROR_RELOAD_THROTTLE_MS = 30 * 1000;

const pageLoadedAt = Date.now();

// Returns true when the action identified by `key` may run now, recording the
// attempt; false while a previous attempt is inside the throttle window or
// when storage is unavailable (skip rather than risk a reload loop).
const tryThrottle = (key: string, windowMs: number): boolean => {
  try {
    const last = Number(sessionStorage.getItem(key)) || 0;
    if (Date.now() - last < windowMs) return false;
    sessionStorage.setItem(key, String(Date.now()));
    return true;
  } catch (error) {
    return false;
  }
};

let registration: ServiceWorkerRegistration | null = null;
// True once this page has started leaving (reload or fresh navigation).
let hasReloaded = false;
let reloadPending = false;
let forceImmediateReload = false;
let lastUpdateCheckAt = 0;
let userHasInteracted = false;
// False while the boot gate holds the splash.
let appRendered = false;
// A new worker took over while the gate was still open.
let controllerChangedDuringGate = false;
// This page was reached by navigateFresh() (the marker was in the URL).
let arrivedViaFreshNav = false;

const reloadOnce = () => {
  if (hasReloaded) return;
  hasReloaded = true;
  window.location.reload();
};

// Leaves for the same URL plus the fresh-navigation marker; the service
// worker lets that request through to the network, which serves the current
// shell. replace() keeps the stale shell out of the history.
const navigateFresh = (serverBuild: number) => {
  if (hasReloaded) return;
  hasReloaded = true;
  const url = new URL(window.location.href);
  url.searchParams.set(FRESH_PARAM, String(serverBuild));
  window.location.replace(url.toString());
};

// Removes the fresh-navigation marker so it never reaches the router or a
// copied link, remembering that this page is the fresh navigation.
const stripFreshParam = () => {
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has(FRESH_PARAM)) return;
    arrivedViaFreshNav = true;
    url.searchParams.delete(FRESH_PARAM);
    window.history.replaceState(window.history.state, '', url.toString());
  } catch (error) {}
};

const checkForUpdate = () => {
  lastUpdateCheckAt = Date.now();
  if (registration) {
    registration.update().catch(() => {});
    return;
  }
  // register() can fail transiently (storage pressure) while an installed
  // worker still controls the page; recover the browser's registration so
  // update checks — and the incompatible-build handshake — keep working.
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker
      .getRegistration()
      .then((existing) => {
        if (existing) {
          registration = existing;
          return existing.update();
        }
        return undefined;
      })
      .catch(() => {});
  }
};

const checkForUpdateThrottled = () => {
  if (Date.now() - lastUpdateCheckAt > VISIBILITY_CHECK_THROTTLE_MS) {
    checkForUpdate();
  }
};

// Resolves with the deployed build id, or null when the server couldn't be
// asked (offline, captive portal, non-JSON answer, deadline passed).
const fetchServerBuild = async (timeoutMs: number): Promise<number | null> => {
  const abort = new AbortController();
  const timer = setTimeout(() => abort.abort(), timeoutMs);
  try {
    const response = await fetch(VERSION_URL, {
      cache: 'no-store',
      credentials: 'omit',
      signal: abort.signal,
    });
    if (!response.ok) return null;
    const data = await response.json();
    const build = Number(data?.build);
    return Number.isFinite(build) && build > 0 ? build : null;
  } catch (error) {
    return null;
  } finally {
    clearTimeout(timer);
  }
};

/**
 * Called by the axios layer when the backend's x-riw-min-fe-build header says
 * this build is older than the minimum the BE supports. Re-checks for the new
 * version and reloads into it the moment it activates.
 */
export const forceUpdateAndReload = () => {
  if (import.meta.env.MODE !== 'production') return;

  forceImmediateReload = true;
  if (reloadPending) {
    reloadOnce();
    return;
  }

  const swActive =
    registration ||
    ('serviceWorker' in navigator && navigator.serviceWorker.controller);
  if (swActive) {
    // The update check finding a new version fires controllerchange, which
    // reloads. Every API response carries the header while this build is
    // stale, so throttle the re-check in case the new FE is still rolling out.
    if (Date.now() - lastUpdateCheckAt > 60 * 1000) {
      checkForUpdate();
    }
    return;
  }

  // No service worker (unsupported browser, or registration failed): a plain
  // reload re-fetches the no-store index.html and boots the new build.
  if (tryThrottle(FORCED_RELOAD_AT_KEY, FORCED_RELOAD_THROTTLE_MS)) {
    reloadOnce();
  }
};

// A newer build is active in the worker and this rendered page runs an older
// one: reload without disrupting the user.
const scheduleReload = () => {
  // Still invisible while the user hasn't touched anything — but never eat
  // their typing 45s in.
  if (
    Date.now() - pageLoadedAt < STARTUP_RELOAD_WINDOW_MS &&
    !userHasInteracted
  ) {
    reloadOnce();
    return;
  }

  // Mid-session update: hold the reload until the tab is hidden so it never
  // yanks the page out from under the user — and never while a request the
  // user just submitted is still in flight (an aborted POST leaves them
  // unsure whether it saved). A skipped attempt retries on the next
  // visibility change.
  reloadPending = true;
  if (document.visibilityState === 'hidden' && !hasPendingRequests()) {
    reloadOnce();
  }
};

const onControllerChange = async () => {
  if (forceImmediateReload) {
    reloadOnce();
    return;
  }

  // Gate still open: the boot check decides (it reloads if the server can't
  // be asked, since the worker evidently has a newer build at hand).
  if (!appRendered) {
    controllerChangedDuringGate = true;
    return;
  }

  // The new worker may just have caught up to the build this page already
  // runs (a network-fresh load after a deploy). Only a different deployed
  // build is worth a reload.
  const serverBuild = await fetchServerBuild(VERSION_CHECK_TIMEOUT_MS);
  if (serverBuild === __BUILD_TIME__) return;
  scheduleReload();
};

// Wires the lifecycle listeners (synchronously, before any await) and
// registers /sw.js. Resolves with the registration, or null when registering
// failed (private mode, storage pressure).
const setupServiceWorker = async (): Promise<ServiceWorkerRegistration | null> => {
  // With clientsClaim, the very first install also fires controllerchange.
  // That's a brand-new visitor getting their first SW — nothing to reload.
  // Only from the second change on (this page was already controlled, or a
  // first-visit page saw its initial claim) does the event mean "new deploy".
  let wasControlled = Boolean(navigator.serviceWorker.controller);

  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!wasControlled) {
      wasControlled = true;
      return;
    }
    onControllerChange();
  });

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      if (reloadPending && !hasPendingRequests()) {
        reloadOnce();
        return;
      }
      // Check while hidden too: a deploy found now installs and reloads the
      // tab before the user looks at it again.
      checkForUpdateThrottled();
      return;
    }
    // Tab (or installed PWA) came back to the foreground: check for a deploy.
    checkForUpdateThrottled();
  });

  setInterval(checkForUpdate, UPDATE_CHECK_INTERVAL_MS);

  try {
    registration = await navigator.serviceWorker.register('/sw.js', {
      updateViaCache: 'none',
    });
    return registration;
  } catch (error) {
    return null;
  }
};

// The boot gate for a page whose shell came from the precache. Resolves when
// the app may render; if it made the page leave, hasReloaded is true.
const runBootGate = async (): Promise<void> => {
  // Listeners attach synchronously; the registration itself may resolve late
  // (the browser queues it behind an in-flight install), so nothing below
  // waits on it.
  setupServiceWorker().then((reg) => {
    if (!reg) return;
    // Re-fetches /sw.js (no-cache); a byte difference installs the new build
    // into the precache in the background.
    lastUpdateCheckAt = Date.now();
    reg.update().catch(() => {});
  });

  const serverBuild = await fetchServerBuild(VERSION_CHECK_TIMEOUT_MS);
  if (hasReloaded) return;

  // Current build: render.
  if (serverBuild === __BUILD_TIME__) return;

  if (serverBuild === null) {
    // The server couldn't be asked. If a new worker took over meanwhile, it
    // has a newer build at hand — reload into it. Otherwise render what we
    // have; a late controller change is handled by onControllerChange.
    if (controllerChangedDuringGate) reloadOnce();
    return;
  }

  // A different build is deployed. This page is itself the fresh navigation
  // yet still mismatches: the network failed and the worker served the
  // precached shell (or the server is mid-deploy). Render what we have — the
  // URL is already stripped, so a later reload gets to try again — rather
  // than navigate in a loop. Otherwise load the new build from the network.
  if (arrivedViaFreshNav) return;
  navigateFresh(serverBuild);
};

const installPageListeners = () => {
  const markInteraction = () => {
    userHasInteracted = true;
  };
  window.addEventListener('pointerdown', markInteraction, {
    once: true,
    capture: true,
  });
  window.addEventListener('keydown', markInteraction, {
    once: true,
    capture: true,
  });

  // A deploy can leave a still-open page unable to load a not-yet-visited
  // lazy route: its old hashed chunks are gone from the precache and from
  // hosting (the SPA rewrite answers with index.html instead). Vite surfaces
  // that as a preload error; reloading boots the current version, where every
  // chunk exists.
  window.addEventListener('vite:preloadError', (event) => {
    if (
      !tryThrottle(PRELOAD_ERROR_RELOAD_AT_KEY, PRELOAD_ERROR_RELOAD_THROTTLE_MS)
    ) {
      return;
    }
    event.preventDefault();
    window.location.reload();
  });
};

/**
 * Resolves when the app may render: this page runs the latest deployed build,
 * or the server couldn't be asked within budget. When a newer build is
 * deployed the page leaves for it instead and the promise stays pending (with
 * a fallback so a navigation that never happens can't strand the user on the
 * splash).
 */
export const bootWithLatestVersion = (): Promise<void> =>
  new Promise<void>((resolve) => {
    const render = () => {
      appRendered = true;
      resolve();
    };

    if (import.meta.env.MODE !== 'production') {
      render();
      return;
    }

    stripFreshParam();
    installPageListeners();

    if (!('serviceWorker' in navigator)) {
      render();
      return;
    }

    // The old updater stored a version marker; it steers nothing anymore.
    try {
      localStorage.removeItem('app-version');
    } catch (error) {}

    if (!navigator.serviceWorker.controller) {
      // First visit or hard reload: the shell came from the network, so it is
      // the deployed build by definition. Register after load so precaching
      // doesn't compete with the app's own initial downloads.
      window.addEventListener('load', () => {
        setupServiceWorker();
      });
      render();
      return;
    }

    // Repeat visit: the shell came from the precache and may be stale.
    runBootGate().then(
      () => {
        if (hasReloaded) {
          setTimeout(render, RELOAD_FALLBACK_MS);
          return;
        }
        render();
      },
      () => render()
    );
  });
